// src/pages/languages/LanguageParentContentForm.jsx
// ─────────────────────────────────────────────────────────────
// فورم موحّد للإضافة والتعديل: قطعة قراءة / تسجيل صوتي / فيديو + أسئلتها.
// بيحل محل AddReadingToLanguage.
//
// Routes (في App.jsx) — لازم key عشان React ما يعيدش استخدام نفس الـ state بين الأنواع:
//   languages/skills/:skillId/reading/create          kind="READING"
//   languages/skills/:skillId/reading/:itemId/edit    kind="READING"
//   ... نفس الشكل لـ listening و speaking
// ─────────────────────────────────────────────────────────────
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowRight, Plus, Trash2, Upload } from "lucide-react";
import {
  getApiError,
  getAllSkillItems,
  languageReadingAPI,
  languageListeningAPI,
  languageSpeakingAPI,
} from "../../services/languageService";
import TranslationField from "../../components/TranslationField";
import ImageField from "../../components/ImageField";
import { withFiles } from "../../utils/withFiles";

const CONFIG = {
  READING: {
    title: "قطعة قراءة",
    api: languageReadingAPI,
    create: (p) => languageReadingAPI.createPassage(p),
    update: (id, p) => languageReadingAPI.updatePassage(id, p),
    resultKey: "passage",
    imageField: "passage_image",
    imageLabel: "صورة القطعة (اختياري)",
    textField: "passage_text",
    textLabel: "نص القطعة",
    textRequired: true,
  },
  LISTENING: {
    title: "تسجيل صوتي",
    api: languageListeningAPI,
    create: (p, cb) => languageListeningAPI.createAudio(p, cb),
    update: (id, p, cb) => languageListeningAPI.updateAudio(id, p, cb),
    resultKey: "audio",
    imageField: "audio_image",
    imageLabel: "صورة التسجيل (اختياري)",
    fileField: "audio_file",
    accept: "audio/*",
    fileLabel: "الملف الصوتي",
    textField: "transcript",
    textLabel: "النص المكتوب (اختياري)",
  },
  SPEAKING: {
    title: "فيديو",
    api: languageSpeakingAPI,
    create: (p, cb) => languageSpeakingAPI.createVideo(p, cb),
    update: (id, p, cb) => languageSpeakingAPI.updateVideo(id, p, cb),
    resultKey: "video",
    imageField: "video_image",
    imageLabel: "صورة مصغّرة للفيديو (اختياري)",
    fileField: "video_file",
    accept: "video/*",
    fileLabel: "ملف الفيديو",
    textField: "description",
    textLabel: "الوصف (اختياري)",
  },
};

const LETTERS = ["A", "B", "C", "D"];
const DIFFICULTIES = ["EASY", "MEDIUM", "HARD"];

const inputCls =
  "w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white";

let uid = 0;
const newQuestion = () => ({
  key: `new-${++uid}`,
  id: null,
  question_text: "",
  options: ["", "", "", ""],
  correct_index: null,
  explanation: "",
  english_explanation: "",
  translation: "",
  image_file: null,
  image_url: "",
  points: 1,
});

const fromServer = (q) => {
  const options = [q.choice_a, q.choice_b, q.choice_c, q.choice_d].map((o) => o || "");
  return {
    key: `q-${q.id}`,
    id: q.id,
    question_text: q.question_text || "",
    options,
    correct_index: LETTERS.indexOf(q.correct_answer) >= 0 ? LETTERS.indexOf(q.correct_answer) : null,
    explanation: q.explanation || "",
    english_explanation: q.english_explanation || "",
    translation: q.translation || "",
    image_file: null,
    image_url: q.question_image || "",
    points: q.points ?? 1,
  };
};

const isBlank = (q) =>
  !q.question_text.trim() && q.options.every((o) => !o.trim());

export default function LanguageParentContentForm({ kind }) {
  const cfg = CONFIG[kind];
  const { skillId, itemId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEdit = Boolean(itemId);

  // لو الأب اتعمل وبعدها سؤال فشل، المحاولة التانية لازم تعدّل مش تنشئ نسخة جديدة
  const parentIdRef = useRef(itemId ? Number(itemId) : null);

  const [form, setForm] = useState({ title: "", text: "", source: "", difficulty: "MEDIUM" });
  const [file, setFile] = useState(null);
  const [mediaUrl, setMediaUrl] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imageUrl, setImageUrl] = useState("");
  const [duration, setDuration] = useState(0);
  const [questions, setQuestions] = useState([newQuestion()]);
  const [deletedIds, setDeletedIds] = useState([]);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");

  const backToSkill = () => navigate(`/dashboard/languages/skills/${skillId}`);

  /* ─────────── تحميل بيانات التعديل ─────────── */
  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;

    const hydrate = (item) => {
      setForm({
        title: item.title || "",
        text: item[cfg.textField] || "",
        source: item.source || "",
        difficulty: item.difficulty || "MEDIUM",
      });
      setMediaUrl(cfg.fileField ? item[cfg.fileField] || "" : "");
      setImageUrl(item[cfg.imageField] || "");
      setDuration(item.duration || 0);
      const qs = (item.questions || []).map(fromServer);
      setQuestions(qs.length ? qs : [newQuestion()]);
    };

    (async () => {
      try {
        const st = location.state?.question;
        let item = st && st.type === kind && String(st.id) === String(itemId) ? st : null;
        if (!item) {
          const all = await getAllSkillItems(skillId);
          item = all.find((i) => i.type === kind && String(i.id) === String(itemId));
        }
        if (cancelled) return;
        if (!item) setError("العنصر غير موجود");
        else hydrate(item);
      } catch (e) {
        if (!cancelled) setError(getApiError(e, "فشل تحميل البيانات"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId, skillId, kind]);

  /* ─────────── الملف + حساب المدة تلقائيًا ─────────── */
  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    const url = URL.createObjectURL(f);
    const el = document.createElement(kind === "LISTENING" ? "audio" : "video");
    el.preload = "metadata";
    el.onloadedmetadata = () => {
      if (Number.isFinite(el.duration)) setDuration(Math.round(el.duration));
      URL.revokeObjectURL(url);
    };
    el.onerror = () => URL.revokeObjectURL(url);
    el.src = url;
  };

  /* ─────────── الأسئلة ─────────── */
  const patchQ = (key, patch) =>
    setQuestions((qs) => qs.map((q) => (q.key === key ? { ...q, ...patch } : q)));

  const setOption = (key, idx, value) =>
    setQuestions((qs) =>
      qs.map((q) =>
        q.key === key ? { ...q, options: q.options.map((o, i) => (i === idx ? value : o)) } : q
      )
    );

  const removeQ = (q) => {
    if (q.id) setDeletedIds((ids) => [...ids, q.id]);
    setQuestions((qs) => {
      const rest = qs.filter((x) => x.key !== q.key);
      return rest.length ? rest : [newQuestion()];
    });
  };

  /* ─────────── الحفظ ─────────── */
  const buildParentPayload = () => {
    if (kind === "READING") {
      return withFiles(
        {
        title: form.title.trim(),
        passage_text: form.text,
        source: form.source,
        difficulty: form.difficulty,
        ...(parentIdRef.current ? {} : { language_skill: Number(skillId) }),
      },
        { [cfg.imageField]: imageFile }
      );
    }
    const fd = new FormData();
    fd.append("title", form.title.trim());
    fd.append(cfg.textField, form.text);
    fd.append("difficulty", form.difficulty);
    if (file) fd.append(cfg.fileField, file);
    if (imageFile) fd.append(cfg.imageField, imageFile);
    if (duration) fd.append("duration", String(duration));
    if (!parentIdRef.current) fd.append("language_skill", String(skillId));
    return fd;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.title.trim()) return setError("العنوان مطلوب");
    if (cfg.textRequired && !form.text.trim()) return setError(`${cfg.textLabel} مطلوب`);
    if (cfg.fileField && !parentIdRef.current && !file) return setError(`${cfg.fileLabel} مطلوب`);

    // تجهيز الأسئلة (الفاضية بتتجاهل)
    const ready = [];
    for (let idx = 0; idx < questions.length; idx++) {
      const q = questions[idx];
      if (isBlank(q) && !q.id) continue;
      const n = idx + 1;
      if (!q.question_text.trim()) return setError(`السؤال ${n}: نص السؤال مطلوب`);
      const options = q.options.map((o) => o.trim()).filter(Boolean);
      if (options.length < 2) return setError(`السؤال ${n}: لازم خيارين على الأقل`);
      const correct = q.correct_index === null ? "" : q.options[q.correct_index].trim();
      if (!correct) return setError(`السؤال ${n}: حدد الإجابة الصحيحة`);
      ready.push({
        idx,
        q,
        payload: withFiles({
          question_text: q.question_text.trim(),
          options,
          correct_answer: correct,
          explanation: q.explanation,
          english_explanation: q.english_explanation,
          translation: q.translation,
          points: Number(q.points) || 1,
          order: idx,
        }, { question_image: q.image_file }),
      });
    }

    setSaving(true);
    setProgress(0);
    const next = [...questions];
    try {
      const payload = buildParentPayload();
      if (parentIdRef.current) {
        await cfg.update(parentIdRef.current, payload, setProgress);
      } else {
        const res = await cfg.create(payload, setProgress);
        parentIdRef.current = (res[cfg.resultKey] || res).id;
      }
      setFile(null);
      setImageFile(null); // الملف اترفع، المحاولة التانية (لو حصلت) متعيدش الرفع

      for (const id of deletedIds) {
        try {
          await cfg.api.deleteQuestion(id);
        } catch (err) {
          if (err?.response?.status !== 404) throw err;
        }
      }
      setDeletedIds([]);

      for (const r of ready) {
        if (r.q.id) {
          await cfg.api.updateQuestion(r.q.id, r.payload);
          next[r.idx] = { ...next[r.idx], image_file: null };
        } else {
          const res = await cfg.api.createQuestion(parentIdRef.current, r.payload);
          next[r.idx] = { ...next[r.idx], id: res.question.id, image_file: null };
        }
      }
      backToSkill();
    } catch (err) {
      console.error(err);
      setQuestions(next);
      setError(getApiError(err, "فشل الحفظ، حاول مرة أخرى"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const showProgress = saving && cfg.fileField && progress > 0 && progress < 100;

  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-w-3xl">
      <div>
        <button
          type="button"
          onClick={backToSkill}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-2"
        >
          <ArrowRight className="w-4 h-4" />
          رجوع للمهارة
        </button>
        <h1 className="text-xl font-bold text-gray-900">
          {isEdit ? "تعديل" : "إضافة"} {cfg.title}
        </h1>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
          {error}
        </div>
      )}

      {/* ─── بيانات العنصر الأب ─── */}
      <div className="card space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">العنوان</label>
          <input
            className={inputCls}
            dir="auto"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />
        </div>

        {cfg.fileField && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{cfg.fileLabel}</label>
            <label className="flex items-center gap-2 border border-dashed border-gray-300 rounded-xl px-4 py-3 text-sm text-gray-600 cursor-pointer hover:bg-gray-50">
              <Upload className="w-4 h-4" />
              <span className="truncate">
                {file ? file.name : mediaUrl ? "تغيير الملف الحالي" : "اختر ملف"}
              </span>
              <input type="file" accept={cfg.accept} onChange={onFile} className="hidden" />
            </label>
            {!file && mediaUrl && kind === "LISTENING" && (
              <audio controls src={mediaUrl} preload="none" className="w-full h-10 mt-2" />
            )}
            {!file && mediaUrl && kind === "SPEAKING" && (
              <video controls src={mediaUrl} preload="none" className="w-full max-h-60 rounded-xl bg-black mt-2" />
            )}
            {duration > 0 && (
              <p className="text-xs text-gray-400 mt-1">المدة: {duration} ثانية</p>
            )}
          </div>
        )}
        <ImageField
          label={cfg.imageLabel}
          file={imageFile}
          existingUrl={imageUrl}
          onChange={setImageFile}
        />

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{cfg.textLabel}</label>
          <textarea
            className={`${inputCls} min-h-[120px]`}
            dir="auto"
            value={form.text}
            onChange={(e) => setForm({ ...form, text: e.target.value })}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {kind === "READING" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">المصدر (اختياري)</label>
              <input
                className={inputCls}
                dir="auto"
                value={form.source}
                onChange={(e) => setForm({ ...form, source: e.target.value })}
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">الصعوبة</label>
            <select
              className={inputCls}
              value={form.difficulty}
              onChange={(e) => setForm({ ...form, difficulty: e.target.value })}
            >
              {DIFFICULTIES.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* ─── الأسئلة ─── */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-bold text-gray-900">الأسئلة</h2>
          <button
            type="button"
            onClick={() => setQuestions((qs) => [...qs, newQuestion()])}
            className="flex items-center gap-1.5 text-blue-600 hover:text-blue-700 text-sm font-medium"
          >
            <Plus className="w-4 h-4" />
            إضافة سؤال
          </button>
        </div>

        {questions.map((q, i) => (
          <div key={q.key} className="card space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-700">سؤال {i + 1}</span>
              <button
                type="button"
                onClick={() => removeQ(q)}
                title="حذف السؤال"
                className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>

            <textarea
              className={`${inputCls} min-h-[70px]`}
              dir="auto"
              placeholder="نص السؤال"
              value={q.question_text}
              onChange={(e) => patchQ(q.key, { question_text: e.target.value })}
            />
            <TranslationField
              label="ترجمة نص السؤال"
              value={q.translation}
              onChange={(v) => ((e) => patchQ(q.key, { translation: e.target.value }))({ target: { value: v } })}
            />
            <ImageField
              label="صورة السؤال (اختياري)"
              file={q.image_file}
              existingUrl={q.image_url}
              onChange={(f) => patchQ(q.key, { image_file: f })}
            />

            <div className="space-y-2" dir="ltr">
              {q.options.map((opt, idx) => {
                const selected = q.correct_index === idx;
                return (
                  <div key={idx} className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => patchQ(q.key, { correct_index: idx })}
                      title="الإجابة الصحيحة"
                      className={`w-7 h-7 rounded-full text-xs font-bold flex-shrink-0 border ${
                        selected
                          ? "bg-emerald-600 border-emerald-600 text-white"
                          : "bg-white border-gray-300 text-gray-500 hover:border-emerald-500"
                      }`}
                    >
                      {LETTERS[idx]}
                    </button>
                    <input
                      className={inputCls}
                      placeholder={`Option ${LETTERS[idx]}`}
                      value={opt}
                      onChange={(e) => setOption(q.key, idx, e.target.value)}
                    />
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-gray-400">اضغط على حرف الخيار لتحديده كإجابة صحيحة.</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <textarea
                className={`${inputCls} min-h-[60px]`}
                dir="rtl"
                placeholder="الشرح (عربي)"
                value={q.explanation}
                onChange={(e) => patchQ(q.key, { explanation: e.target.value })}
              />
              <textarea
                className={`${inputCls} min-h-[60px]`}
                dir="ltr"
                placeholder="Explanation (English)"
                value={q.english_explanation}
                onChange={(e) => patchQ(q.key, { english_explanation: e.target.value })}
              />
            </div>

            <div className="w-32">
              <label className="block text-xs text-gray-500 mb-1">النقاط</label>
              <input
                type="number"
                min={1}
                className={inputCls}
                value={q.points}
                onChange={(e) => patchQ(q.key, { points: e.target.value })}
              />
            </div>
          </div>
        ))}
      </div>

      {showProgress && (
        <div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full bg-blue-600 transition-all" style={{ width: `${progress}%` }} />
          </div>
          <p className="text-xs text-gray-500 mt-1">جاري رفع الملف... {progress}%</p>
        </div>
      )}

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={saving}
          className="px-5 py-2.5 rounded-xl text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? "جاري الحفظ..." : isEdit ? "حفظ التعديلات" : "حفظ"}
        </button>
        <button
          type="button"
          onClick={backToSkill}
          disabled={saving}
          className="px-5 py-2.5 rounded-xl text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-50"
        >
          إلغاء
        </button>
      </div>
    </form>
  );
}